import { z } from 'zod';
// ─── Scope: откуда берём контекст ──────────────────────────────────────
export const AiViewScope = z.object({
    kind: z.literal('view'),
    viewId: z.string().uuid(),
    userFilters: z.array(z.record(z.unknown())).optional(),
});
export const AiItemScope = z.object({
    kind: z.literal('item'),
    itemId: z.string().uuid(),
});
/** Свободный чат без контекста представления/записи. */
export const AiGeneralScope = z.object({
    kind: z.literal('general'),
});
export const AiScope = z.discriminatedUnion('kind', [AiViewScope, AiItemScope, AiGeneralScope]);
// ─── Presets ────────────────────────────────────────────────────────────
export const AiPreset = z.enum([
    'summary_quarter',
    'suggest_category',
    'clarify_title',
    'clarify_criteria',
    'summarize_comments',
    'jira_description',
    'analysis_backlog',
    'quarterly_report',
]);
// ─── Chat request / SSE events ─────────────────────────────────────────
/** Одно сообщение из истории чата. */
export const AiHistoryMessageSchema = z.object({
    role: z.enum(['user', 'assistant']),
    content: z.string().max(32000),
});
export const AiChatRequestSchema = z.object({
    scope: AiScope,
    message: z.string().min(1),
    preset: AiPreset.optional(),
    /** Разрешить LLM вызывать инструменты (создание, обновление, удаление записей). */
    enableTools: z.boolean().optional(),
    /** Предыдущие сообщения чата (для поддержания контекста разговора). */
    history: z.array(AiHistoryMessageSchema).max(50).optional(),
    /** ID беседы — если указан, сообщения сохраняются в эту беседу. */
    conversationId: z.string().uuid().optional(),
    /** Override модели LLM (id из /api/ai/models). Если не задано — используется дефолт из platform settings/env. */
    model: z.string().min(1).max(200).optional(),
});
// SSE event types sent to the client
export const AiSseMetaSchema = z.object({
    type: z.literal('meta'),
    contextSize: z.number(),
    contextTotal: z.number().optional(),
    truncated: z.boolean().optional(),
    scopeKind: z.enum(['general', 'view', 'item']).optional(),
    preset: AiPreset.nullable().optional(),
    decompositionApplied: z.boolean().optional(),
    decompositionSteps: z.number().int().nonnegative().optional(),
});
export const AiSseDeltaSchema = z.object({
    type: z.literal('delta'),
    text: z.string(),
});
export const AiSseDoneSchema = z.object({
    type: z.literal('done'),
    usage: z.object({
        promptTokens: z.number(),
        completionTokens: z.number(),
    }),
    /** ID беседы — возвращается при сохранении истории. */
    conversationId: z.string().uuid().optional(),
});
export const AiSseErrorSchema = z.object({
    type: z.literal('error'),
    code: z.string(),
    message: z.string().optional(),
});
// Tool-calling SSE events
export const AiSseToolCallSchema = z.object({
    type: z.literal('tool_call'),
    toolCallId: z.string(),
    name: z.string(),
    arguments: z.record(z.unknown()),
});
export const AiSseToolResultSchema = z.object({
    type: z.literal('tool_result'),
    toolCallId: z.string(),
    name: z.string(),
    success: z.boolean(),
    summary: z.string(),
});
/** P2.3 — прогресс долгих bulk-операций (например, create 100 items). */
export const AiSseToolProgressSchema = z.object({
    type: z.literal('tool_progress'),
    toolCallId: z.string(),
    name: z.string(),
    message: z.string(),
    current: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});
// ─── Item suggest ──────────────────────────────────────────────────────
export const AiSuggestKind = z.enum([
    'clarify_title',
    'clarify_criteria',
    'suggest_category',
    'summarize_comments',
]);
export const AiSuggestRequestSchema = z.object({
    kind: AiSuggestKind,
    fieldSlug: z.string().min(1).max(120).optional(),
    fieldName: z.string().min(1).max(200).optional(),
});
// ─── Jira description (non-SSE, JSON response) ────────────────────────
export const AiJiraDescriptionResponseSchema = z.object({
    description: z.string(),
    status: z.literal('success'),
});
// ─── Conversations (история чатов) ────────────────────────────────────
/** Краткая информация о беседе (для списка). */
export const AiConversationSummarySchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    spaceId: z.string().uuid().nullable(),
    viewId: z.string().uuid().nullable(),
    updatedAt: z.string(),
    messageCount: z.number(),
});
/** Часть сообщения (text / tool_call / tool_result). */
export const AiMessagePartSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('text'), content: z.string() }),
    z.object({
        type: z.literal('tool_call'),
        info: z.object({
            toolCallId: z.string(),
            name: z.string(),
            args: z.record(z.unknown()),
        }),
    }),
    z.object({
        type: z.literal('tool_result'),
        info: z.object({
            toolCallId: z.string(),
            name: z.string(),
            success: z.boolean(),
            summary: z.string(),
        }),
    }),
]);
/** Полное сообщение из БД. */
export const AiPersistedMessageSchema = z.object({
    id: z.string().uuid(),
    role: z.enum(['user', 'assistant']),
    parts: z.array(AiMessagePartSchema),
    createdAt: z.string(),
});
/** Полная беседа с сообщениями. */
export const AiConversationDetailSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    spaceId: z.string().uuid().nullable(),
    viewId: z.string().uuid().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    messages: z.array(AiPersistedMessageSchema),
});
// ─── Error codes ───────────────────────────────────────────────────────
export const AiErrorCode = z.enum([
    'context_too_large',
    'scope_empty',
    'llm_unavailable',
    'llm_timeout',
    'llm_rate_limited',
    'rate_limited',
    'forbidden_scope',
    'invalid_preset',
    'ai_not_configured',
    'internal',
]);
